// 아바타 장부(레인 E, docs/AVATAR-ADVENTURE-PRD.md 1.8, 새 키 dungeonhero.avatar.v1 — 캐릭터별 진행 키, account/keys.ts SYNC_KEYS).
// 형식 { v:1, owned:[id], equip:{부위:id}, hide, shards:{지역:수}, got:[연산 id], owed:[{op,gold}] }. 쓰기 전에 다시 읽어 다른 탭의 변경을 합친다(RaidStore와 같은 방식).
// 키가 없으면(처음) 시작 지급 3종을 넣는다. 깨졌으면 시작 지급만 다시 들어간다(장비·진행은 다른 키). 이전 빌드는 이 키를 모른다(롤백 안전).
// owed(미정산 제작 골드)는 이 빌드부터의 필드다. 구버전 탭이 정산 전 짧은 틈에 이 키를 다시 쓰면 owed가 빠질 수 있다(제작 직후 바로 정산하므로 창은 수 ms — 롤백 전용 위험으로 수용, PR #262 리뷰 P2-1).
import { AVATAR_SLOTS, AVATAR_START_IDS, avatarDef, craftRecipe, type AvatarCraftCode, makeAvatarTotals, sumAvatar, type AvatarSlot, type AvatarTotals } from "../shared/avatar";
import type { SaveStorage } from "./save";

export const AVATAR_KEY = "dungeonhero.avatar.v1";
/** 같은 드롭 연산 id 중복 방지로 기억하는 수 */
export const AVATAR_GOT_CAP = 200;

export type AvatarCode = "ok" | "unknown" | "not_owned" | "write_failed" | "owned" | "no_shards" | "dup";
/** 드롭 결과: new = 새 아바타, shard = 이미 있어 조각으로, dup = 이미 받은 연산 */
export type AvatarGain = "new" | "shard" | "dup" | "write_failed";

interface AvatarData { owned: string[]; equip: Partial<Record<AvatarSlot, string>>; hide: boolean; shards: Record<string, number>; got: string[];
  /** 제작 골드 미정산 장부(제작 쓰기와 같은 쓰기로 들어가고, 골드 결제가 확인되면 지운다). 이전 빌드는 모르는 필드라 롤백 안전 */
  owed: AvatarOwed[] }
export interface AvatarOwed { op: string; gold: number }
/** 미정산 장부 최대 수 */
export const AVATAR_OWED_CAP = 50;

function fresh(): AvatarData { return { owned: [...AVATAR_START_IDS], equip: {}, hide: false, shards: {}, got: [], owed: [] }; }

export class AvatarStore {
  private data: AvatarData = fresh();
  /** 바뀔 때마다 1 오른다(UI 다시 그리기·능력치 다시 계산) */
  revision = 0;
  readonly totals: AvatarTotals = makeAvatarTotals();
  /** 부위 순서(AVATAR_SLOTS)대로 장착 id(없으면 null) — 렌더·UI가 읽는다 */
  readonly equipped: (string | null)[] = AVATAR_SLOTS.map(() => null);
  /** 마지막 획득(UI 획득 띠): 아바타 id·결과·순번 */
  lastGain: { id: string; gain: "new" | "shard"; serial: number } | null = null;
  private gainSerial = 0;
  /** 마지막 제작 시도(결과 띠): 성공·실패 모두 */
  lastCraft: { id: string; code: AvatarCraftCode; serial: number } | null = null;
  private craftSerial = 0;

  constructor(private readonly storage: SaveStorage) { this.read(); }

  get owned(): readonly string[] { return this.data.owned; }
  get owed(): readonly AvatarOwed[] { return this.data.owed; }
  get hide(): boolean { return this.data.hide; }
  get shards(): Readonly<Record<string, number>> { return this.data.shards; }
  has(id: string): boolean { return this.data.owned.includes(id); }

  /** 저장이 이 빌드보다 새 판본(v > 1)이면 읽기만 하고 쓰지 않는다(새 빌드 데이터를 덮어쓰지 않게) */
  private readonly_ = false;

  /** 저장에서 다시 읽는다(다른 탭 변경 반영). 키가 없을 때만 시작 지급을 쓴다 */
  read(): void {
    let raw: string | null;
    // 읽기 예외(일시적 저장소 오류)는 "키 없음"과 다르다: 기존 값을 시작 지급으로 덮어쓰지 않고 지금 상태를 유지한다
    try { raw = this.storage.getItem(AVATAR_KEY); } catch { this.derive(); return; }
    const parsed = raw ? parse(raw) : null;
    this.readonly_ = parsed?.future === true;
    if (parsed) this.data = parsed.data;
    else {
      this.data = fresh();
      // 처음(또는 깨짐): 시작 지급을 바로 저장해 새로고침마다 다시 만들지 않는다(고정 id라 다시 들어가도 같은 결과)
      this.write();
    }
    this.derive();
  }

  private derive(): void {
    for (let i = 0; i < AVATAR_SLOTS.length; i++) {
      const id = this.data.equip[AVATAR_SLOTS[i]];
      this.equipped[i] = id && this.has(id) && avatarDef(id)?.slot === AVATAR_SLOTS[i] ? id : null;
    }
    sumAvatar(this.totals, this.equipped);
    this.revision++;
  }

  private write(): boolean {
    if (this.readonly_) return false;
    try {
      this.storage.setItem(AVATAR_KEY, JSON.stringify({ v: 1, owned: this.data.owned, equip: this.data.equip, hide: this.data.hide, shards: this.data.shards, got: this.data.got, owed: this.data.owed }));
      return true;
    } catch { return false; }
  }

  /** 변경 하나: 다시 읽고 → 바꾸고 → 쓴다. 쓰기 실패면 되돌린다 */
  private mutate(fn: (d: AvatarData) => AvatarCode): AvatarCode {
    this.read();
    const before = JSON.stringify(this.data);
    const code = fn(this.data);
    if (code !== "ok") return code;
    if (!this.write()) { this.data = JSON.parse(before) as AvatarData; this.derive(); return "write_failed"; }
    this.derive();
    return "ok";
  }

  equip(id: string): AvatarCode {
    const d = avatarDef(id);
    if (!d) return "unknown";
    return this.mutate((x) => { if (!x.owned.includes(id)) return "not_owned"; x.equip[d.slot] = id; return "ok"; });
  }
  unequip(slot: AvatarSlot): AvatarCode {
    if (!AVATAR_SLOTS.includes(slot)) return "unknown";
    return this.mutate((x) => { delete x.equip[slot]; return "ok"; });
  }
  setHide(hide: boolean): AvatarCode { return this.mutate((x) => { x.hide = hide; return "ok"; }); }

  /** 보스 드롭 넣기. opId가 같으면 한 번만(두 탭·재시도). 이미 가진 아바타는 그 지역 조각 1개 */
  grant(opId: string, id: string): AvatarGain {
    const d = avatarDef(id);
    if (!d) return "dup";
    let gain = "dup" as AvatarGain;
    const code = this.mutate((x) => {
      if (x.got.includes(opId)) { gain = "dup"; return "unknown"; }
      x.got.push(opId);
      if (x.got.length > AVATAR_GOT_CAP) x.got.splice(0, x.got.length - AVATAR_GOT_CAP);
      if (x.owned.includes(id)) { const r = String(d.source.region ?? 0); x.shards[r] = (x.shards[r] ?? 0) + 1; gain = "shard"; }
      else { x.owned.push(id); gain = "new"; }
      return "ok";
    });
    if (code === "write_failed") return "write_failed";
    if (gain === "new" || gain === "shard") this.lastGain = { id, gain, serial: ++this.gainSerial };
    return gain;
  }

  /**
   * 제작(E3): 그 지역 조각을 차감하고 아바타를 넣는다 — 한 번의 쓰기로 같이(쓰기 실패면 mutate가 되돌린다).
   * 같은 쓰기에 미정산 골드 {op, gold}도 적는다 — 골드 결제는 GameSim.settleAvatarOwed가 나중에(곧바로) 하고 확인되면 clearOwed로 지운다(환불 경로 없음).
   * opId가 같으면 한 번만("dup"): 재시도·두 탭이 두 번 만들지 않는다
   */
  craft(id: string, opId: string, gold: number): AvatarCode {
    const r = craftRecipe(id);
    if (!r) return "unknown";
    return this.mutate((x) => {
      if (x.got.includes(opId)) return "dup";
      if (x.owned.includes(id)) return "owned";
      const k = String(r.region);
      if ((x.shards[k] ?? 0) < r.shards) return "no_shards";
      const left = x.shards[k] - r.shards;
      if (left > 0) x.shards[k] = left; else delete x.shards[k];
      x.owned.push(id);
      x.got.push(opId);
      if (x.got.length > AVATAR_GOT_CAP) x.got.splice(0, x.got.length - AVATAR_GOT_CAP);
      if (gold > 0 && !x.owed.some((o) => o.op === opId)) x.owed.push({ op: opId, gold });
      return "ok";
    });
  }
  /** 골드 결제가 확인된 미정산 항목을 지운다(쓰기 실패면 false — 항목이 남아 다음에 다시 정산하고, 장부가 duplicate로 막는다) */
  clearOwed(op: string): boolean {
    let found = false;
    const code = this.mutate((x) => { const n = x.owed.length; x.owed = x.owed.filter((o) => o.op !== op); found = x.owed.length !== n; return found ? "ok" : "unknown"; });
    return code === "ok" || !found;
  }
  /** 보상으로 한 번만 주기(도감 보상): 이미 가졌거나 같은 opId면 아무것도 바꾸지 않는다(조각으로 바꾸지 않음). ok·owned·dup 모두 "받은 상태" */
  award(id: string, opId: string): AvatarCode {
    if (!avatarDef(id)) return "unknown";
    return this.mutate((x) => {
      if (x.got.includes(opId)) return "dup";
      if (x.owned.includes(id)) return "owned";
      x.owned.push(id);
      x.got.push(opId);
      if (x.got.length > AVATAR_GOT_CAP) x.got.splice(0, x.got.length - AVATAR_GOT_CAP);
      return "ok";
    });
  }
  /** 제작 결과 띠 기록(성공·실패) */
  noteCraft(id: string, code: AvatarCraftCode): void { this.lastCraft = { id, code, serial: ++this.craftSerial }; this.revision++; }
}

/** 모르는 아바타 id(새 빌드가 넣은 것)도 보유 목록에는 남긴다 — 이 빌드가 다시 써도 지우지 않게. 표시·합산은 정의가 있는 것만 */
const ID_RE = /^av_[a-z0-9_]{1,40}$/;

function parse(raw: string): { data: AvatarData; future: boolean } | null {
  try {
    const v = JSON.parse(raw) as Record<string, unknown>;
    if (!v || typeof v.v !== "number" || v.v < 1 || !Array.isArray(v.owned)) return null;
    const owned = [...new Set((v.owned as unknown[]).filter((id): id is string => typeof id === "string" && ID_RE.test(id)))];
    const equip: Partial<Record<AvatarSlot, string>> = {};
    if (v.equip && typeof v.equip === "object") for (const s of AVATAR_SLOTS) {
      const id = (v.equip as Record<string, unknown>)[s];
      if (typeof id === "string" && owned.includes(id) && avatarDef(id)?.slot === s) equip[s] = id;
    }
    const shards: Record<string, number> = {};
    if (v.shards && typeof v.shards === "object") for (const [k, n] of Object.entries(v.shards as Record<string, unknown>)) {
      if (/^[0-9]{1,2}$/.test(k) && typeof n === "number" && Number.isFinite(n) && n > 0) shards[k] = Math.min(9999, Math.floor(n));
    }
    const got = Array.isArray(v.got) ? (v.got as unknown[]).filter((id): id is string => typeof id === "string" && id.length < 120).slice(-AVATAR_GOT_CAP) : [];
    const owed: AvatarOwed[] = [];
    if (Array.isArray(v.owed)) for (const o of v.owed as unknown[]) {
      const r = o as Record<string, unknown> | null;
      if (r && typeof r.op === "string" && r.op.length > 0 && r.op.length < 120 && typeof r.gold === "number" && Number.isInteger(r.gold) && r.gold > 0 && owed.length < AVATAR_OWED_CAP && !owed.some((q) => q.op === r.op)) owed.push({ op: r.op, gold: r.gold });
    }
    return { data: { owned, equip, hide: v.hide === true, shards, got, owed }, future: v.v > 1 };
  } catch { return null; }
}
